import { MouseButton, UUID } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import {
	ObjectCustomName,
	RendererMode,
	ThreeEvent,
	UserDataObjectType,
	UserDataProp,
} from '../types';
import { CursorHandler } from '../utils/CursorHandler';
import { CameraType, RoomDragType } from '@/types/creator';
import { Vector2, Raycaster, Vector3, Plane, Mesh } from 'three';
import { RoomWall } from '../objects/RoomWall';
import { LandscapeModule } from '../objects/LandscapeModule';
import { DraggableObject } from '../objects/DraggableObject';
import { ProductModule } from '../objects/ProductModule';
import { RoomFloor } from '../objects/RoomFloor';
import { MouseDragHandler } from '../utils/MouseDragHandler';
import { ModulesGrid } from '../utils/ModulesGrid';
import { RenderUtils } from '../utils/RenderUtils';
import { ModulesHistorySnapshot } from '../managers/ModulesHistoryManager';

export class Mouse {
	private mouse = new Vector2();
	private raycaster = new Raycaster();
	private enabled = true;
	private dragBeforeSnapshot: ModulesHistorySnapshot | null = null;
	dragOffset = new Vector3();
	mouseDragHandler: MouseDragHandler;
	mouseDownScenePos = new Vector3();
	scenePos = new Vector3();
	plane = new Plane(new Vector3(0, 1, 0));
	isRoomHovered = false;
	isRoomDragging = false;
	cursorHandler: CursorHandler;
	hoveredObject: DraggableObject | null = null;
	hoveredObjectPart: Mesh | null = null;

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
		this.mouseMove = this.mouseMove.bind(this);
		this.onMouseDown = this.onMouseDown.bind(this);
		this.onMouseUp = this.onMouseUp.bind(this);

		this.cursorHandler = new CursorHandler(renderer, this);
		this.mouseDragHandler = new MouseDragHandler(renderer, this);
	}

	private get historyManager() {
		return this.renderer.modulesManager.historyManager;
	}

	private mouseMove(event: MouseEvent) {
		event.preventDefault();
		if (!this.enabled) return;
		if (this.historyManager.isRestoring) return;

		const rect = this.renderer.canvas!.getBoundingClientRect();
		this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		this.raycaster.setFromCamera(
			this.mouse,
			this.renderer.cameraController.activeCamera === CameraType.ORTHOGRAPHIC
				? this.renderer.cameraController.orthographicCamera
				: this.renderer.cameraController.perspectiveCamera
		);
		this.raycaster.ray.intersectPlane(this.plane, this.scenePos);

		if (event.shiftKey) this.snapPosToGrid();

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.snapPosToGrid();
				this.renderer.drawingManager.handleMouseMove([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			case RendererMode.COLORING:
				this.checkColoringIntersection();
				break;
			case RendererMode.ROOM:
			case RendererMode.MODULES:
				if (this.isRoomDragging) {
					this.mouseDragHandler.handleRoomDrag();
					break;
				}

				if (this.mouseDragHandler.draggingGroup.children.length) {
					this.mouseDragHandler.handleGroupDragging();
					break;
				}

				if (this.mouseDragHandler.draggedObject) {
					this.mouseDragHandler.handleDragging();
					break;
				}

				this.renderer.unHoverAllObjects();
				this.checkIntersections();
				break;
			case RendererMode.MEASURING:
				this.renderer.measurementManager.handleMouseMove([
					this.scenePos.x,
					this.scenePos.z,
				]);
				break;
			default:
				break;
		}

		this.cursorHandler.setCursorType();
	}

	private onMouseDown(event: MouseEvent) {
		if (!this.enabled) return;
		if (this.historyManager.isRestoring) return;

		this.mouseDownScenePos.copy(this.scenePos);

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.renderer.drawingManager.handleMouseDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			case RendererMode.COLORING:
				if (!this.hoveredObjectPart) return;

				this.renderer.modulesManager.coloringManager.handleColorChange(
					this.hoveredObject as ProductModule,
					this.hoveredObjectPart
				);
				return;
			case RendererMode.ROOM:
			case RendererMode.COLORING:
			case RendererMode.MODULES:
				if (event.button === MouseButton.MIDDLE) return;

				if (event.button === MouseButton.RIGHT) {
					if (!this.hoveredObject) return;

					this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
						id: this.hoveredObject.id,
						position: [event.clientX, event.clientY],
						moduleLevel: this.hoveredObject.level,
					});
					return;
				}

				if (this.isRoomHovered) {
					this.isRoomDragging = true;
					break;
				}

				this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
					id: null,
					position: [0, 0],
					moduleLevel: 0,
				});

				if (this.mouseDragHandler.draggedProductModules.length) {
					for (const productModule of this.mouseDragHandler
						.draggedProductModules) {
						this.renderer.room.attach(productModule.group);
						const worldPos = new Vector3();
						productModule.group.getWorldPosition(worldPos);
						productModule.group.position.copy(worldPos);
						productModule.snapToGrid();
						this.renderer.modulesManager.modulesGrid.registerModule(
							productModule
						);
					}

					this.mouseDragHandler.draggingGroup.clear();
					this.mouseDragHandler.draggingGroup.position.set(0, 0, 0);
					this.mouseDragHandler.draggedProductModules = [];
					this.historyManager.commitGroupTransaction();
					return;
				}

				if (!this.hoveredObject) return;

				this.dragBeforeSnapshot = this.historyManager.capture();

				if (this.hoveredObject instanceof ProductModule)
					this.renderer.modulesManager.modulesGrid.unregisterModule(
						this.hoveredObject
					);

				this.renderer.cameraController.perspectiveControls.enableRotate = false;
				this.mouseDragHandler.draggedObject = this.hoveredObject;
				this.mouseDragHandler.draggedObject.isDragging = true;

				this.dragOffset = this.mouseDownScenePos
					.clone()
					.sub(this.mouseDragHandler.draggedObject.group.position);

				break;
			case RendererMode.MEASURING:
				if (event.button === MouseButton.MIDDLE) return;
				if (event.button === MouseButton.RIGHT) return;

				this.renderer.measurementManager.handleMouseDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				break;
			default:
				break;
		}

		this.cursorHandler.setCursorType();
	}

	private onMouseUp(event: MouseEvent) {
		event.preventDefault();
		if (!this.enabled) return;

		this.mouseDownScenePos.set(0, 0, 0);
		this.dragOffset.set(0, 0, 0);

		if (this.isRoomDragging) {
			this.isRoomDragging = false;
			this.mouseDragHandler.handleRoomDragEnd();
		}

		if (this.mouseDragHandler.draggedObject) {
			this.renderer.cameraController.perspectiveControls.enableRotate = true;

			const draggedObject = this.mouseDragHandler.draggedObject;

			if (draggedObject instanceof ProductModule) {
				this.renderer.modulesManager.modulesGrid.registerModule(draggedObject);
				this.renderer.modulesManager.coloringManager.applyBarsColorsAfterJoin(
					draggedObject.id
				);
			}

			if (draggedObject instanceof RoomWall)
				this.renderer.roomManager.flushModulesHistoryReset();

			draggedObject.isDragging = false;
			this.mouseDragHandler.draggedObject = null;

			if (
				this.dragBeforeSnapshot &&
				(draggedObject instanceof ProductModule ||
					draggedObject instanceof LandscapeModule)
			)
				this.historyManager.commit(this.dragBeforeSnapshot);

			this.dragBeforeSnapshot = null;
		}

		this.cursorHandler.setCursorType();
	}

	private checkIntersections() {
		const intersects = this.raycaster.intersectObjects(
			this.renderer.room.children
		);

		const intersectedObject = intersects[0]?.object;

		if (!intersectedObject) {
			this.hoveredObject = null;
			this.isRoomHovered = false;
			this.hoveredObjectPart = null;
			return;
		}

		this.isRoomHovered = intersectedObject.name === ObjectCustomName.ROOM_FLOOR;

		const objectType = intersectedObject.userData[UserDataProp.OBJECT_TYPE];
		if (!objectType) return;

		switch (objectType) {
			case UserDataObjectType.WALL:
				const wall = this.renderer.roomManager.getWall(
					intersectedObject.userData[UserDataProp.OBJECT_ID] as UUID
				);
				if (!wall) return;

				wall.isMouseOver = true;
				this.hoveredObject = wall;
				break;
			case UserDataObjectType.LANDSCAPE_MODULE:
			case UserDataObjectType.PRODUCT_MODULE:
				const hoveredObject = this.renderer.modulesManager.getModule(
					intersectedObject.userData[UserDataProp.OBJECT_ID] as UUID
				) as ProductModule | LandscapeModule | undefined;
				if (!hoveredObject) return;

				if (hoveredObject instanceof LandscapeModule) {
					hoveredObject.isMouseOver = true;
					this.hoveredObject = hoveredObject;
					break;
				}

				const isEnabled =
					this.renderer.cameraController.activeCamera ===
						CameraType.PERSPECTIVE ||
					hoveredObject.checkIsAnyBlockOnCurrentLevel();

				if (!isEnabled) break;

				hoveredObject.isMouseOver = true;
				this.hoveredObject = hoveredObject;
				break;

			default:
				throw new Error('INVALID_OBJECT_TYPE');
		}
	}

	private checkColoringIntersection() {
		const intersects = this.raycaster.intersectObjects(
			this.renderer.room.children
		);

		const intersectedObject = intersects[0]?.object;

		if (!intersectedObject) {
			this.hoveredObject = null;
			this.isRoomHovered = false;
			this.hoveredObjectPart = null;
			return;
		}

		if (!intersectedObject.userData[UserDataProp.IS_COLORABLE]) return;
		if (!RenderUtils.isMesh(intersectedObject)) return;

		const hoveredObject = this.renderer.modulesManager.getModule(
			intersectedObject.userData[UserDataProp.OBJECT_ID] as UUID
		) as ProductModule | LandscapeModule | undefined;
		if (!hoveredObject) return;

		this.hoveredObjectPart = intersectedObject;
		this.hoveredObject = hoveredObject;
		return;
	}

	private snapPosToGrid() {
		this.scenePos.x =
			Math.round(this.scenePos.x / ModulesGrid.GRID_UNIT) *
			ModulesGrid.GRID_UNIT;
		this.scenePos.y =
			Math.round(this.scenePos.y / ModulesGrid.GRID_UNIT) *
			ModulesGrid.GRID_UNIT;
		this.scenePos.z =
			Math.round(this.scenePos.z / ModulesGrid.GRID_UNIT) *
			ModulesGrid.GRID_UNIT;
	}

	private setRoomLayers() {
		if (this.renderer.cameraController.activeCamera === CameraType.PERSPECTIVE)
			return;

		switch (this.renderer.roomManager.roomDragType) {
			case RoomDragType.WALL:
				if (this.renderer.roomManager.wallsVisible)
					this.raycaster.layers.enable(RoomWall.LAYER);
				break;
			case RoomDragType.ROOM:
				this.raycaster.layers.enable(RoomFloor.LAYER);
				break;
		}
	}

	private enableModulesLayers() {
		this.raycaster.layers.enable(LandscapeModule.LAYER);
		this.raycaster.layers.enable(ProductModule.LAYER);
	}

	cancelActiveInteractions() {
		this.dragBeforeSnapshot = null;
		this.isRoomDragging = false;
		this.hoveredObject = null;
		this.hoveredObjectPart = null;
		this.isRoomHovered = false;
		this.mouseDragHandler.cancelActiveDrag();
		this.historyManager.cancelGroupTransaction();
	}

	setAvailability() {
		this.raycaster.layers.disableAll();
		this.enabled = false;

		switch (this.renderer.mode) {
			case RendererMode.ROOM:
				this.enabled = true;
				this.enableModulesLayers();
				this.setRoomLayers();
				break;
			case RendererMode.COLORING:
			case RendererMode.MODULES:
				this.enabled = true;
				this.setRoomLayers();
				this.enableModulesLayers();
				break;
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.enabled = true;
				break;
			case RendererMode.PREVIEW:
				break;
			case RendererMode.MEASURING:
				this.enabled = true;
				break;
		}
	}

	setDragginGroup(productModules: ProductModule[]) {
		for (const productModule of productModules) {
			this.renderer.modulesManager.modulesGrid.unregisterModule(productModule);
			this.mouseDragHandler.draggedProductModules.push(productModule);
			this.mouseDragHandler.draggingGroup.attach(productModule.group);
		}
	}

	init() {
		this.raycaster.layers.disableAll();

		this.renderer.canvas!.addEventListener('mousemove', this.mouseMove);
		this.renderer.canvas!.addEventListener('mousedown', this.onMouseDown);
		this.renderer.canvas!.addEventListener('mouseup', this.onMouseUp);

		this.setAvailability();
	}
}
